import type { PropsWithChildren, ReactElement } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthentication, AuthenticationStatus } from '@mysaiva/core/authentication'
import { LoadingPage } from '@mysaiva/core/loading'
import { NoAccessPage } from '@mysaiva/core/components'
import { getBackToSaivaPath, useDeveloperPlatformAccess } from '@mysaiva/core/developer-platform'

/**
 * Route-level guard for `/integrations/*` (SAIV-11354).
 *
 * Order matters:
 * 1. auth still resolving → loading
 * 2. not signed in → `/login`, original location preserved
 * 3. children (org + app context providers, then `AccessGate`)
 *
 * The LaunchDarkly rollout flag (SAIV-11355) is temporarily not enforced so
 * the workspace runs without a LaunchDarkly client id; access is decided by
 * `AccessGate` alone. Re-add the flag check here when the rollout resumes.
 */
export function DeveloperPlatformGate({ children }: PropsWithChildren): ReactElement {
  const { user, status } = useAuthentication()
  const location = useLocation()

  if (status === AuthenticationStatus.Loading) {
    return <LoadingPage />
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  return <>{children}</>
}

/**
 * Permission check, evaluated once the organisation context exists.
 * Users without developer-platform access get a standard "no access" page
 * (never a broken layout) with a way back to their product.
 */
export function AccessGate({ children }: PropsWithChildren): ReactElement {
  const { t } = useTranslation()
  const { hasAccess, isLoading } = useDeveloperPlatformAccess()

  if (isLoading) {
    return <LoadingPage />
  }

  if (!hasAccess) {
    return (
      <NoAccessPage
        title={t('developerPlatform.access.deniedTitle')}
        message={t('developerPlatform.access.deniedMessage')}
        buttonLabel={t('developerPlatform.backToSaiva')}
        onButtonClick={() => window.location.assign(getBackToSaivaPath())}
      />
    )
  }

  return <>{children}</>
}
